import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import { BehaviorSubject, combineLatest, map, Observable, Subscription, take } from 'rxjs';
import { select, Store } from '@ngrx/store';

import { BoundType, PaxAmount } from '@fcom/dapi';
import { ButtonPosition } from '@fcom/ui-components';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { Bound, Cabin, FareInformation, FinnairCabinClass } from '@fcom/dapi/api/models';
import { ExtendedFareFamily, FareFamilyBadgeType } from '@fcom/common-booking/index';
import { GlobalBookingFlight, globalBookingFlights } from '@fcom/common/store';
import { CabinClassModalComponent } from '@fcom/common-booking/components/flight-selection/cabin-class-modal/cabin-class-modal.component';
import { GtmService } from '@fcom/common/gtm';
import {
  GtmEvent,
  GtmMulticityFareFamilySelectionData,
  GtmPurchaseFlow,
  OfferListFetchParams,
} from '@fcom/common/interfaces';
import { combineFlightDates, combineOriginDestination } from '@fcom/common/utils/gtm.utils';
import { isShortHaulFlight } from '@fcom/common/utils';

import { BookingMultiCityService } from '../../services';
import {
  selectMultiCityAvailableFareFamilies,
  selectMultiCityCurrency,
  selectMultiCityFareFamiliesPerCabin,
  selectMultiCitySelectedBounds,
} from '../../../../store/selectors';
import { CountryCode } from '../../../../utils';
import { getPreselectedFareFamily } from '../../utils/offer.utils';

@Component({
  selector: 'fin-multi-city-fare-family',
  templateUrl: './multi-city-fare-family.component.html',
  standalone: false,
})
export class MultiCityFareFamilyComponent implements OnInit {
  public ButtonPosition = ButtonPosition;

  private subscriptions = new Subscription();

  @Input() paxAmount$: Observable<PaxAmount>;

  @Input() flightSearch$: Observable<OfferListFetchParams>;

  @Output() continueToMultiCityBounds = new EventEmitter<ExtendedFareFamily>();

  fareFamilies$: Observable<ExtendedFareFamily[]>;
  selectedFareFamily$ = new BehaviorSubject<ExtendedFareFamily>(null);
  currency$: Observable<string>;
  showKoreaNotification$: Observable<boolean>;
  availableCabins$: Observable<Cabin[]>;

  @ViewChild(CabinClassModalComponent)
  private cabinClassModal: CabinClassModalComponent;
  selectedBounds$: Observable<Bound[]>;
  flights$: Observable<GlobalBookingFlight[]>;
  isAllShortHaul$: Observable<boolean>;

  constructor(
    private store$: Store<BookingAppState>,
    private bookingMultiCityService: BookingMultiCityService,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.fareFamilies$ = combineLatest([
      this.store$.pipe(select(selectMultiCityAvailableFareFamilies)),
      this.store$.pipe(select(selectMultiCityFareFamiliesPerCabin)),
      this.selectedFareFamily$,
    ]).pipe(
      map(([fareFamilies, fareFamiliesPerCabin, selectedFareFamily]) => {
        return fareFamilies.map((fareFamily) => {
          const cabinClass = this.bookingMultiCityService.getCabinForFareFamily(
            fareFamily.fareFamilyCode,
            fareFamiliesPerCabin
          ) as FareInformation['cabinClass'];

          return {
            id: `${fareFamily.fareFamilyCode}-multi`,
            fareFamilyCode: fareFamily.fareFamilyCode,
            fareInformation: [
              {
                bookingClass: cabinClass,
                cabinClass,
                fareBasisCode: null,
                segmentId: `${fareFamily.fareFamilyCode}-multi`,
              },
            ],
            benefits: fareFamily,
            price: fareFamily.totalPrice,
            totalPrice: fareFamily.totalPrice,
            isStartingFromPrice: true,
            disabled: false,
            selected: fareFamily.fareFamilyCode === selectedFareFamily?.fareFamilyCode,
            badgeType:
              fareFamily.fareFamilyCode === getPreselectedFareFamily(cabinClass, fareFamilies)?.fareFamilyCode
                ? FareFamilyBadgeType.POPULAR
                : null,
          };
        });
      })
    );

    this.currency$ = this.store$.pipe(select(selectMultiCityCurrency));

    this.selectedBounds$ = this.store$.select(selectMultiCitySelectedBounds);

    this.flights$ = this.store$.pipe(globalBookingFlights());

    this.isAllShortHaul$ = this.flights$.pipe(
      map((flights) => flights.every((flight) => isShortHaulFlight(flight.origin, flight.destination)))
    );

    this.showKoreaNotification$ = this.flights$.pipe(
      map((flights) => {
        return flights[0].origin.countryCode === CountryCode.KOREA;
      })
    );

    this.availableCabins$ = this.fareFamilies$.pipe(
      map((fareFamilies) => {
        return fareFamilies.reduce((acc, fareFamily) => {
          const cabinClass = fareFamily.fareInformation[0].cabinClass as FinnairCabinClass;
          const availableCabins = [FinnairCabinClass.ECONOMY, FinnairCabinClass.ECOPREMIUM, FinnairCabinClass.BUSINESS];

          if (!acc.includes(cabinClass) && availableCabins.includes(cabinClass)) {
            acc.push(cabinClass);
          }

          return acc;
        }, []);
      })
    );
  }

  selectFareFamily(selectedFareFamily: { fareFamily: ExtendedFareFamily; checked: boolean }): void {
    this.subscriptions.add(
      this.store$.pipe(select(selectMultiCityAvailableFareFamilies), take(1)).subscribe((fareFamilies) => {
        if (!selectedFareFamily.checked) {
          this.selectedFareFamily$.next(null);

          return;
        }

        this.selectedFareFamily$.next(
          fareFamilies?.find((fareFamily) => {
            return fareFamily.fareFamilyCode === selectedFareFamily.fareFamily.fareFamilyCode;
          })
        );
      })
    );
  }

  continueWithSelection(): void {
    const selectedFareFamily = this.selectedFareFamily$.getValue();
    this.continueToMultiCityBounds.emit(selectedFareFamily);

    this.sendSelectedFareFamilySelection(selectedFareFamily);
  }

  sendSelectedFareFamilySelection(fareFamily: ExtendedFareFamily): void {
    this.subscriptions.add(
      combineLatest([this.flightSearch$, this.paxAmount$, this.currency$])
        .pipe(take(1))
        .subscribe(([params, paxAmount, currency]) => {
          const enhancedData: GtmMulticityFareFamilySelectionData = {
            boundType: BoundType.multi,
            fareFamilyName: fareFamily.brandName,
            travelClass: params.cabin,
            currencyCode: currency,
            price: fareFamily.totalPrice,
            points: null,
            paxAmount: (paxAmount.adults + paxAmount.children + paxAmount.infants).toString(),
            purchaseFlow: GtmPurchaseFlow.BOOKING,
            dates: combineFlightDates(params.flights),
            route: combineOriginDestination(params.flights),
            stage: 'fareFamilySelection',
          };

          this.gtmService.multicityFareFamilySelection(GtmEvent.FLIGHT_SELECTION_CLICK, enhancedData);
        })
    );
  }

  openCabinClassModal(cabinClass: string): void {
    this.cabinClassModal.openModal(null, cabinClass.toLowerCase());
  }
}
